import graphql__cf5740b2b64f46730fce from "./__generated__/ContributorContactModal_contribution.graphql.js";import { useCallback, useMemo, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { defineMessages } from "react-intl";
import classNames from "classnames";
import { useFormatMessageWithCustomization } from "../../Intl/Context";
import { isUnauthorizedOrUnavailable } from "../../Utils/authorizations";

import Modal, { ModalContext } from "../../Modals/Modal";
import Button from "../../Buttons/Button";
import FormSubmitButton from "../../ParticipativeModule/FormSubmitButton";
import { sendContributorContact } from "../../../mutations/SendContributorContactMutation";
import { useAlert } from "../../Alerts/withAlert";
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from "../../Alerts/Alert";

const ContributorContactModal = props => {
  const contribution = useFragment(
    ContributorContactModal_contribution,
    props.contribution
  );

  const translateWithCustomization = useFormatMessageWithCustomization(
    contribution.participativeModule.visualization.customIntlMessages
  );
  const title = translateWithCustomization(intlMessages.title);
  const description = translateWithCustomization(intlMessages.description);
  const validateButtonLabel = translateWithCustomization(
    intlMessages.validateButtonLabel
  );
  const cancelButtonLabel = translateWithCustomization(intlMessages.cancelButtonLabel);
  const signInMessage = translateWithCustomization(intlMessages.signInMessage);

  const { contributorContactAuthorized } = contribution.authorizations;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showAlert } = useAlert();
  const closeModalRef = useRef(() => {});

  const handleContactResponse = useCallback(
    ({ sendContributorContact: { success, notice } }) => {
      setIsSubmitting(false);

      if (success) {
        showAlert({ message: notice, type: ALERT_TYPE_SUCCESS });
        closeModalRef.current && closeModalRef.current();
      } else {
        showAlert({ message: notice, type: ALERT_TYPE_ERROR });
      }
    },
    [showAlert]
  );

  const handleConfirmButtonClicked = useCallback(() => {
    setIsSubmitting(true);

    sendContributorContact(contribution.id, handleContactResponse);
  }, [contribution.id, handleContactResponse]);

  const authenticationAdditionalProps = useMemo(
    () => ({ contributionId: contribution.id, forceEmailForAnonymousUsers: true }),
    [contribution.id]
  );

  if (isUnauthorizedOrUnavailable(contributorContactAuthorized)) return null;

  const className = classNames("contributor-contact-modal", "modal-sm", props.className);

  return (
    <Modal
      isOpen={true}
      shouldCloseOnOverlayClick={true}
      className={className}
      title={title}
    >
      <ModalContext.Consumer>
        {({ closeModal }) => {
          closeModalRef.current = closeModal;

          return (
            <>
              <div
                className="box padding-x-lg padding-y-md margin-bottom-xs"
                dangerouslySetInnerHTML={{ __html: description }}
              />

              <div className="modal-actions modal-buttons-container">
                <FormSubmitButton
                  onSignIn={handleConfirmButtonClicked}
                  submitLabel={validateButtonLabel}
                  signInMessage={signInMessage}
                  authorization={contributorContactAuthorized}
                  isSubmitting={isSubmitting}
                  additionalProps={authenticationAdditionalProps}
                />

                <Button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-primary-inverse btn-block-md box-shadow-lg modal-action"
                  label={cancelButtonLabel}
                  iconClassName="icon-suppr"
                  iconPosition="after"
                  data-loading={isSubmitting}
                  disabled={isSubmitting}
                />
              </div>
            </>
          );
        }}
      </ModalContext.Consumer>
    </Modal>
  );
};

export default ContributorContactModal;

const ContributorContactModal_contribution = graphql__cf5740b2b64f46730fce;

const intlMessages = defineMessages({
  title: {
    id: "participativeModule.contributorsContact.title",
    defaultMessage: "Mise en relation avec le contributeur",
    description: "Titre de la fenêtre de mise en relation"
  },
  description: {
    id: "participativeModule.contributorsContact.description",
    defaultMessage: `
      Vous allez envoyer une demande de mise en relation avec le contributeur. Lorsque 
      vous validerez la demande avec le bouton ci-dessous, si vous n'êtes pas déjà 
      authentifié, vous serez invité à renseigner vos informations d'identification, 
      notamment votre adresse e-mail. Votre adresse e-mail sera ensuite envoyée au
      contributeur qui pourra alors vous contacter directement en retour.
    `,
    description: "Contenu explicatif de la fenêtre de mise en relation"
  },
  validateButtonLabel: {
    id: "participativeModule.contributorsContact.validateButtonLabel",
    defaultMessage: "Valider la demande de mise en relation",
    description: "Libellé du bouton de validation de la mise en relation"
  },
  cancelButtonLabel: {
    id: "participativeModule.contributorsContact.cancelButtonLabel",
    defaultMessage: "Annuler",
    description: "Libellé du bouton d'annulation de la mise en relation"
  },
  signInMessage: {
    id: "participativeModule.contributorsContact.signInMessage",
    defaultMessage: `
      Merci de vous identifier afin de valider votre demande de mise en relation.
    `,
    description: `
      Message affiché lorsque l'utilisateur valide la mise en relation mais doit 
      s'authentifier avant
    `
  }
});
